import { authService } from './authService';
import { CORE_API_URL } from '../config';

// Candidate Graph client.
//
// Points at the Go API (cmd/api), not the Python one: fit-v2.0 is computed in Go
// and served from score.candidate_fit. The legacy /api/candidates/graph endpoint
// on the Python api reads the retired score.candidate_graph snapshot and is no
// longer called from here.
//
// Note what the responses do NOT carry: a fit score. Ranking happens server-side
// and the number is deliberately never sent, so this client cannot display it
// even by accident.

async function request(path) {
  const token = authService.requireToken();

  let response;
  try {
    response = await fetch(`${CORE_API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    throw new Error(
      `Network error: unable to reach the candidate API at ${CORE_API_URL}. ` +
        'Check that the Go api service is running.'
    );
  }

  if (!response.ok) {
    let detail = `API returned status ${response.status}`;
    try {
      const body = await response.json();
      detail = body.detail || detail;
    } catch (e) {
      /* keep the status-code message */
    }
    throw new Error(detail);
  }
  return response.json();
}

export const candidateService = {
  // Returns { candidates: [...], total, buckets: { call_now, warm, track, low } }.
  // The bucket counts describe the whole book and deliberately do not move with
  // the filters.
  //
  // dims picks which desk dimensions apply: both (send nothing) | territory | sector | none.
  // Sending nothing is deliberate -- the server defaults to the WHOLE desk, so a
  // caller that forgets cannot silently turn scoping off
  // (Task 5). Omitting it is what makes the server apply the desk by default,
  // so it is only ever added when true -- never sent as an explicit "apply".
  async getCandidates({ bucket = '', search = '', country = '', limit = 50, offset = 0, dims = 'both' } = {}) {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    if (bucket) params.append('bucket', bucket);
    if (search) params.append('search', search);
    if (country) params.append('country', country);
    if (dims !== 'both') params.append('scope', dims === 'none' ? 'all' : dims);
    return request(`/api/candidates?${params.toString()}`);
  },

  async getCandidateProfile(personId) {
    return request(`/api/candidates/${personId}`);
  },

  // CLASSIC shape — the pre-redesign table at /candidates/classic. This is the
  // only call that returns a fit score, and it is a separate endpoint for that
  // reason: getCandidates() above cannot start returning one by accident.
  // Returns { candidates: [...], total, scoring_config }.
  async getCandidatesClassic({ bucket = '', search = '', country = '', limit = 50, offset = 0, dims = 'both' } = {}) {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    params.append('offset', String(offset));
    if (bucket) params.append('bucket', bucket);
    if (search) params.append('search', search);
    if (country) params.append('country', country);
    if (dims !== 'both') params.append('scope', dims === 'none' ? 'all' : dims);
    return request(`/api/candidates/classic?${params.toString()}`);
  },

  async getCandidateClassicProfile(personId) {
    return request(`/api/candidates/classic/${personId}`);
  },

  // The records behind ONE reason on ONE candidate. Returns
  // { code, total, rows: [{primary, secondary, at}] } — total is the true count
  // while rows is capped, so the dialog can say "showing 50 of 423" instead of
  // truncating silently.
  //
  // Only ask for reasons the API marked `drillable`; the others answer 400,
  // deliberately, because their sentence already states everything.
  async getCandidateEvidence(personId, code, { limit = 50, offset = 0, dims = 'both' } = {}) {
    const params = new URLSearchParams({
      person_id: personId,
      code,
      limit: String(limit),
      offset: String(offset),
    });
    if (dims !== 'both') params.append('scope', dims === 'none' ? 'all' : dims);
    return request(`/api/candidates/evidence?${params.toString()}`);
  },
};
